'use client'
import { useState, useEffect } from 'react'
import { Link } from '@/i18n/routing'
import { login } from './actions'
import SignInWithGoogleButton from "./AuthGoogleButton"
import { useTranslations, useLocale } from 'next-intl' 
import SignInWithTwitterButton from "./AuthXButton"
import { useSearchParams } from 'next/navigation'

export default function Login() {
  const t = useTranslations('login')
  const locale = useLocale()
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showResetLink, setShowResetLink] = useState(false)
  const searchParams = useSearchParams()
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null)
  
  useEffect(() => {
    // Get the redirect URL from query params
    const redirectTo = searchParams.get('redirectTo')
    if (redirectTo) {
      // Validate the redirect URL (optional security measure)
      try {
        // Make sure it's a relative URL that starts with /
        if (redirectTo.startsWith('/')) {
          setRedirectUrl(redirectTo)
        }
      } catch (e) {
        console.error('Invalid redirect URL', e)
      }
    }
  }, [searchParams])

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const formData = new FormData(event.currentTarget)
    const email = formData.get('email') as string
    try {
      setIsLoading(true)
      setError('')
      const result = await login(formData)

      if (result?.error?.toLowerCase().includes('email not confirmed')) {
        setError(t('error.emailNotConfirmed'))
        setTimeout(() => {
          window.location.href = `/${locale}/verify-email?email=${encodeURIComponent(email)}`
        }, 1500)
        return
      }

      if (result?.error) {
        setError(result.error)
        setShowResetLink(true)
        return
      }

      // Redirect to the original URL if available, otherwise to dashboard
      const destinationUrl = redirectUrl ? `${redirectUrl}` : `/${locale}/dashboard`
      window.location.href = destinationUrl
    } catch (err) {
      setError(t('error.default'))
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-bg-main via-primary to-primary">
      <div className="max-w-md w-full mx-4">
        {/* Navigation Spacer */}
        <div className="h-[92px] w-full" />

        <div className="bg-surface rounded-xl backdrop-blur-sm shadow-xl p-8">
          <div className="relative">
            <h2 className="text-2xl font-bold text-center mb-8 text-primary">
              {t('title')}
            </h2>
            
            {error && (
              <div className="mb-6 p-4 border border-status-error bg-status-error/30 rounded-lg text-text-main">
                {error}
              </div>
            )}

            <form onSubmit={onSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-text-main mb-1">
                  {t('form.email.label')}
                </label>
                <div className="mt-2">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="bg-surface-hover shadow-sm block w-full sm:text-sm rounded-md p-2 text-text-main focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder={t('form.email.placeholder')}
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-text-main mb-1">
                  {t('form.password.label')}
                </label>
                <div className="mt-2">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    className="bg-surface-hover shadow-sm block w-full sm:text-sm rounded-md p-2 text-text-main focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder={t('form.password.placeholder')}
                    disabled={isLoading}
                  />
                </div>
              </div>

              {showResetLink && (
                <div className="text-sm text-left -top-4 relative">
                  <Link 
                    href="/auth/reset-password"
                    className="text-text-secondary hover:text-text-main"
                  >
                    {t('form.resetLink')}
                  </Link>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-4 rounded-full bg-primary text-text-main font-medium hover:bg-primary-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? t('form.submit.loading') : t('form.submit.default')}
              </button>

                <SignInWithGoogleButton/>
                <SignInWithTwitterButton/>

              <div className="text-center mt-4">
                <span className="text-text-main">{t('signup.prompt')}</span>
                {' '}
                <Link 
                  href="/signup"
                  className="text-primary hover:text-primary-dark font-medium"
                >
                  {t('signup.link')}
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}